import { Controller } from '@hotwired/stimulus'

// Gallery carousel controller - slide-based navigation with autoplay
export default class extends Controller {
  static targets = ['slides', 'dots', 'thumbnails']
  static values = {
    autoplay: { type: Boolean, default: true },
    interval: { type: Number, default: 5000 }
  }

  connect() {
    this.currentIndex = 0
    this.slideCount = this.getSlideCount()

    if (this.slideCount > 0) {
      this.updateUI()
      this.setupTouchEvents()

      if (this.autoplayValue && this.slideCount > 1) {
        this.startAutoplay()
      }
    }

    // Pause autoplay on hover
    this.element.addEventListener('mouseenter', () => this.pauseAutoplay())
    this.element.addEventListener('mouseleave', () => this.resumeAutoplay())
  }

  disconnect() {
    this.stopAutoplay()
  }

  getSlideCount() {
    if (!this.hasSlidesTarget) return 0
    return this.slidesTarget.children.length
  }

  prev() {
    this.goToIndex((this.currentIndex - 1 + this.slideCount) % this.slideCount)
  }

  next() {
    this.goToIndex((this.currentIndex + 1) % this.slideCount)
  }

  goTo(event) {
    const index = parseInt(event.params.index, 10)
    if (!isNaN(index)) {
      this.goToIndex(index)
    }
  }

  goToIndex(index) {
    if (index < 0 || index >= this.slideCount) return

    this.currentIndex = index
    this.updateUI()

    // Reset autoplay timer when manually navigating
    if (this.autoplayValue) {
      this.resetAutoplay()
    }
  }

  updateUI() {
    // Move slides
    if (this.hasSlidesTarget) {
      const offset = this.currentIndex * 100
      this.slidesTarget.style.transform = `translateX(-${offset}%)`
    }

    // Update dot indicators
    if (this.hasDotsTarget) {
      const dots = this.dotsTarget.querySelectorAll('button')
      dots.forEach((dot, idx) => {
        const isActive = idx === this.currentIndex
        dot.style.backgroundColor = isActive
          ? 'var(--color-primary)'
          : 'var(--color-border)'
        dot.style.width = isActive ? '2rem' : '0.75rem'
      })
    }

    // Update thumbnails
    if (this.hasThumbnailsTarget) {
      const thumbs = this.thumbnailsTarget.querySelectorAll('button')
      thumbs.forEach((thumb, idx) => {
        const isActive = idx === this.currentIndex
        thumb.classList.toggle('border-primary', isActive)
        thumb.classList.toggle('ring-2', isActive)
        thumb.classList.toggle('ring-primary/20', isActive)
        thumb.classList.toggle('border-transparent', !isActive)
        thumb.classList.toggle('opacity-60', !isActive)
        thumb.style.borderColor = isActive ? 'var(--color-primary)' : ''
      })
    }
  }

  setupTouchEvents() {
    if (!this.hasSlidesTarget) return

    let startX = 0
    let startY = 0
    let isDragging = false

    this.slidesTarget.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        isDragging = true
      },
      { passive: true }
    )

    this.slidesTarget.addEventListener(
      'touchmove',
      (e) => {
        if (!isDragging) return

        const diffX = e.touches[0].clientX - startX
        const diffY = e.touches[0].clientY - startY

        // If horizontal swipe is greater than vertical, prevent scroll
        if (Math.abs(diffX) > Math.abs(diffY)) {
          e.preventDefault()
        }
      },
      { passive: false }
    )

    this.slidesTarget.addEventListener('touchend', (e) => {
      if (!isDragging) return
      isDragging = false

      const endX = e.changedTouches[0].clientX
      const diff = startX - endX
      const threshold = 50

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.next()
        } else {
          this.prev()
        }
      }
    })
  }

  startAutoplay() {
    this.autoplayTimer = setInterval(() => {
      this.next()
    }, this.intervalValue)
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  pauseAutoplay() {
    this.stopAutoplay()
    this.isPaused = true
  }

  resumeAutoplay() {
    this.isPaused = false
    if (this.autoplayValue && this.slideCount > 1) {
      this.startAutoplay()
    }
  }

  resetAutoplay() {
    this.stopAutoplay()
    if (!this.isPaused && this.autoplayValue && this.slideCount > 1) {
      this.startAutoplay()
    }
  }
}
